import React from 'react';
import { 
  Users, 
  TrendingUp, 
  DollarSign, 
  CheckCircle,
  XCircle,
  FileText,
  MessageSquare,
  AlertTriangle,
  Package,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const StatsCard = ({ title, value, icon, change, color = 'primary', format = 'number' }) => {
  const getIcon = () => {
    switch (icon) {
      case 'users': return <Users className="h-5 w-5" />;
      case 'trending': return <TrendingUp className="h-5 w-5" />;
      case 'dollar': return <DollarSign className="h-5 w-5" />;
      case 'check': return <CheckCircle className="h-5 w-5" />;
      case 'x': return <XCircle className="h-5 w-5" />;
      case 'file': return <FileText className="h-5 w-5" />;
      case 'message': return <MessageSquare className="h-5 w-5" />;
      case 'alert': return <AlertTriangle className="h-5 w-5" />;
      case 'package': return <Package className="h-5 w-5" />;
      default: return <TrendingUp className="h-5 w-5" />;
    }
  };

  const getColorClasses = () => {
    switch (color) {
      case 'primary': return 'from-primary-500 to-primary-600';
      case 'green': return 'from-green-500 to-green-600';
      case 'red': return 'from-red-500 to-red-600';
      case 'blue': return 'from-blue-500 to-blue-600';
      case 'yellow': return 'from-yellow-500 to-yellow-600';
      case 'purple': return 'from-purple-500 to-purple-600';
      default: return 'from-primary-500 to-primary-600';
    }
  };

  const getIconBgColor = () => {
    switch (color) {
      case 'primary': return 'bg-primary-100';
      case 'green': return 'bg-green-100';
      case 'red': return 'bg-red-100';
      case 'blue': return 'bg-blue-100';
      case 'yellow': return 'bg-yellow-100';
      case 'purple': return 'bg-purple-100';
      default: return 'bg-primary-100';
    }
  };

  const getIconColor = () => {
    switch (color) {
      case 'primary': return 'text-primary-600';
      case 'green': return 'text-green-600';
      case 'red': return 'text-red-600';
      case 'blue': return 'text-blue-600';
      case 'yellow': return 'text-yellow-600';
      case 'purple': return 'text-purple-600';
      default: return 'text-primary-600';
    }
  };

  const formatValue = (val) => {
    if (format === 'currency') {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 2,
      }).format(val);
    }
    
    if (format === 'percent') {
      return `${val}%`;
    }
    
    if (typeof val === 'number' && val >= 1000) {
      return `${(val / 1000).toFixed(1)}k`;
    }
    
    return val;
  };

  return (
    <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30 hover:shadow-neomorph-hover transition-all">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{title}</p>
          <p className="text-lg font-bold text-gray-800 mt-0.5">
            {formatValue(value)}
          </p>
          {change !== undefined && (
            <div className={`flex items-center text-[10px] mt-0.5 ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change >= 0 ? (
                <ArrowUp className="h-2.5 w-2.5 mr-0.5" />
              ) : (
                <ArrowDown className="h-2.5 w-2.5 mr-0.5" />
              )}
              <span className="font-medium">{Math.abs(change)}%</span>
              <span className="text-gray-400 ml-0.5">vs last month</span>
            </div>
          )}
        </div>
        <div className={`p-2 rounded-xl bg-gradient-to-br ${getColorClasses()} shadow-md`}>
          <div className="text-white">
            {getIcon()}
          </div>
        </div>
      </div>
      
      <div className="mt-3 w-full bg-gray-100 rounded-full h-1 shadow-neomorph-inset">
        <div 
          className={`bg-gradient-to-r ${getColorClasses()} h-1 rounded-full transition-all duration-500`}
          style={{ width: `${Math.min(100, Math.random() * 100)}%` }}
        />
      </div>
    </div>
  );
};

export default StatsCard;